import { createRouter, createWebHistory} from 'vue-router';

import { useGeneralStore } from '@/stores/general';
import CheckoutAddonsView from '@/views/CheckoutAddonsView.vue';
import CheckoutInformationView from '@/views/CheckoutInformationView.vue';
import CheckoutPaymentView from '@/views/CheckoutPaymentView.vue';
import CheckoutReceiptView from '@/views/CheckoutReceiptView.vue';
import CheckoutSeatSelectionView from '@/views/CheckoutSeatSelectionView.vue';
import ContactView from '@/views/ContactView.vue';
import NotFoundView from '@/views/NotFoundView.vue';
import ResendTicketsView from '@/views/ResendTicketsView.vue';
import TermsView from '@/views/TermsView.vue';

import CheckoutTicketsView from '../views/CheckoutTicketsView.vue';
import EventDetailView from '../views/EventDetailView.vue';
import EventListView from '../views/EventListView.vue';

// NOTE see src/App.vue for dynamic route additions based on fetched data

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
  routes: [
    {
      path: '/',
      name: 'event-list',
      alias: '/home',
      component: EventListView,
    },
    {
      path: '/event/:id/:name?', 
      name: 'event-detail',
      component: EventDetailView,
    },
    {
      path: '/checkout/tickets',
      name: 'checkout-tickets',
      component: CheckoutTicketsView,
    },
    {
      path: '/checkout/addons',
      name: 'checkout-addons',
      component: CheckoutAddonsView,
    },
    {
      path: '/checkout/seats',
      name: 'checkout-seats',
      component: CheckoutSeatSelectionView,
    },
    {
      path: '/checkout/information',
      name: 'checkout-information',
      component: CheckoutInformationView,
    },
    {
      path: '/checkout/payment',
      name: 'checkout-payment',
      alias: '/paymentfailed',
      component: CheckoutPaymentView,
    },
    {
      path: '/checkout/receipt',
      name: 'checkout-receipt',
      alias: '/receipt',
      component: CheckoutReceiptView,
    },
    {
      path: '/terms',
      name: 'terms',
      component: TermsView,
    },
    {
      path: '/contact',
      name: 'contact',
      component: ContactView,
    },
    {
      path: '/resend-tickets',
      name: 'resend-tickets',
      component: ResendTicketsView,
    },
    {
      path: '/:notFound',
      component: NotFoundView,
    },
  ],
});

router.afterEach((to, from) => {
  localStorage.setItem('lastVisitedRoute', from.fullPath);
});

router.beforeEach((to, from, next) => {
  // Add event name to URL if missing
  if (to.name === 'event-detail' && to.params.id && !to.params.name) {
    const generalStore = useGeneralStore();
    const rawId = to.params.id as string;
    const parsedId = /^\d+$/.test(rawId) ? parseInt(rawId, 10) : null;
    const event = parsedId !== null ? generalStore.events().find(event => event.id === parsedId) : undefined;

    if (event) {
      const eventName = event.info1.replace(/\s+/g, '_');

      // Redirect to the same route with the event name added.
      // replace: true only when the user landed directly on /event/:id via the address bar (from.name === null).
      // In that case the nameless URL is the live browser entry and must be replaced to prevent a back-button
      // loop (/event/:id → guard → /event/:id/name → back → /event/:id → loop).
      // For in-SPA navigations the guard aborts the /event/:id navigation before it is ever committed to
      // history, so replace is not needed — the originating page (e.g. frontpage) stays in the history stack.
      next({
        name: to.name as string,
        params: {
          ...to.params,
          name: eventName,
        },
        replace: from.name === null,
      });
    } else {
      next(); // Proceed if the event is not found
    }
  } else {
    next();
  }
});

export default router;
